(function () {
  'use strict';

  const ROUTINE_LABELS = {
    cleanse: 'Cleanse',
    target: 'Target',
    hydrate: 'Hydrate',
  };

  class SkinQuestionnaire extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.intro = this.querySelector('[data-quiz-view="intro"]');
      this.form = this.querySelector('[data-quiz-form]');
      this.results = this.querySelector('[data-quiz-view="results"]');
      this.questions = Array.from(this.querySelectorAll('[data-question]'));
      this.nextButton = this.querySelector('[data-quiz-next]');
      this.backButton = this.querySelector('[data-quiz-back]');
      this.validationMessage = this.querySelector('[data-validation-message]');
      this.progressText = this.querySelector('[data-progress-text]');
      this.progressPercent = this.querySelector('[data-progress-percent]');
      this.progressBar = this.querySelector('[data-progress-bar]');
      this.productResults = this.querySelector('[data-product-results]');
      this.treatmentResults = this.querySelector('[data-treatment-results]');
      this.treatmentsGroup = this.querySelector('[data-treatments-group]');
      this.treatmentHeading = this.querySelector('[data-treatment-heading]');
      this.pregnancyWarning = this.querySelector('[data-pregnancy-warning]');
      this.cartStatus = this.querySelector('[data-cart-status]');
      this.addAllButton = this.querySelector('[data-add-all]');
      this.currentStep = 0;

      try {
        this.config = JSON.parse(this.querySelector('[data-quiz-config]').textContent);
      } catch (error) {
        console.error('Skin finder configuration could not be read.', error);
        return;
      }

      this.productsByKey = new Map(this.config.products.map((product) => [product.key, product]));
      this.treatmentsByKey = new Map(this.config.treatments.map((treatment) => [treatment.key, treatment]));
      this.consultationsByKey = new Map(
        this.config.consultations.map((consultation) => [consultation.key, consultation])
      );

      this.querySelector('[data-quiz-start]').addEventListener('click', () => this.start());
      this.nextButton.addEventListener('click', () => this.next());
      this.backButton.addEventListener('click', () => this.back());
      this.querySelector('[data-quiz-restart]').addEventListener('click', () => this.restart());
      this.addAllButton.addEventListener('click', () => this.addAllProducts());
      this.form.addEventListener('change', () => this.onAnswerChange());
      this.productResults.addEventListener('click', (event) => this.onProductAction(event));
    }

    start() {
      this.intro.hidden = true;
      this.results.hidden = true;
      this.form.hidden = false;
      this.currentStep = 0;
      this.showStep(true);
    }

    restart() {
      this.form.reset();
      this.currentStep = 0;
      this.results.hidden = true;
      this.intro.hidden = false;
      this.form.hidden = true;
      this.productResults.replaceChildren();
      this.treatmentResults.replaceChildren();
      this.cartStatus.hidden = true;
      this.querySelector('[data-quiz-start]').focus();
    }

    currentAnswer() {
      return this.questions[this.currentStep].querySelector('input[type="radio"]:checked');
    }

    onAnswerChange() {
      this.nextButton.disabled = !this.currentAnswer();
      this.validationMessage.hidden = true;
    }

    next() {
      if (!this.currentAnswer()) {
        this.validationMessage.hidden = false;
        return;
      }

      if (this.currentStep === this.questions.length - 1) {
        this.showResults();
        return;
      }

      this.currentStep += 1;
      this.showStep(true);
    }

    back() {
      if (this.currentStep === 0) return;
      this.currentStep -= 1;
      this.showStep(true);
    }

    showStep(moveFocus) {
      this.questions.forEach((question, index) => {
        question.hidden = index !== this.currentStep;
      });

      const questionNumber = this.currentStep + 1;
      const percent = Math.round((questionNumber / this.questions.length) * 100);
      this.progressText.textContent = `Question ${questionNumber} of ${this.questions.length}`;
      this.progressPercent.textContent = `${percent}%`;
      this.progressBar.style.width = `${percent}%`;
      this.backButton.hidden = this.currentStep === 0;
      this.nextButton.textContent = this.currentStep === this.questions.length - 1 ? 'See my results' : 'Continue';
      this.nextButton.disabled = !this.currentAnswer();
      this.validationMessage.hidden = true;

      if (moveFocus) this.questions[this.currentStep].querySelector('legend').focus({ preventScroll: true });
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    getAnswers() {
      const data = new FormData(this.form);
      return Object.fromEntries(data.entries());
    }

    showResults() {
      if (!window.SkinQuizEngine) {
        this.validationMessage.textContent = 'The skin finder could not load. Please refresh the page and try again.';
        this.validationMessage.hidden = false;
        return;
      }

      const recommendation = window.SkinQuizEngine.evaluate(this.getAnswers());
      this.currentRecommendation = recommendation;
      this.renderProducts(recommendation.products);
      this.renderTreatments(recommendation.treatments, recommendation.consultations);
      this.pregnancyWarning.hidden = recommendation.warning !== 'pregnancy';

      this.form.hidden = true;
      this.intro.hidden = true;
      this.results.hidden = false;
      this.cartStatus.hidden = true;
      this.results.querySelector('.skin-quiz__results-title').focus({ preventScroll: true });
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    renderProducts(keys) {
      this.productResults.replaceChildren();
      const products = keys.map((key) => this.productsByKey.get(key)).filter(Boolean);
      const grid = document.createElement('div');
      grid.className = 'skin-quiz__product-grid';
      products.forEach((product) => grid.append(this.createProductCard(product)));
      this.productResults.append(grid);

      this.addAllButton.disabled = !products.some((product) => product.available);
    }

    createProductCard(product) {
      const card = document.createElement('article');
      card.className = 'skin-quiz__product-card';

      const link = document.createElement('a');
      link.className = 'skin-quiz__product-image';
      link.href = product.url;

      if (product.image) {
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = product.imageAlt || product.title;
        image.loading = 'lazy';
        image.width = 640;
        image.height = 640;
        link.append(image);
      }

      const content = document.createElement('div');
      content.className = 'skin-quiz__product-content';

      const step = document.createElement('p');
      step.className = 'skin-quiz__card-kicker';
      step.textContent = ROUTINE_LABELS[product.step] || ROUTINE_LABELS.target;

      const title = document.createElement('h5');
      const titleLink = document.createElement('a');
      titleLink.href = product.url;
      titleLink.textContent = product.title;
      title.append(titleLink);

      const price = document.createElement('p');
      price.className = 'skin-quiz__product-price';
      price.textContent = product.price;

      const actions = document.createElement('div');
      actions.className = 'skin-quiz__product-actions';

      const addButton = document.createElement('button');
      addButton.className = 'button';
      addButton.type = 'button';
      addButton.dataset.addProduct = product.key;
      addButton.textContent = product.available ? 'Add to cart' : 'Unavailable';
      addButton.disabled = !product.available;
      actions.append(addButton);

      content.append(step, title, price, actions);
      card.append(link, content);
      return card;
    }

    renderTreatments(treatmentKeys, consultationKeys) {
      this.treatmentResults.replaceChildren();
      const cards = [
        ...treatmentKeys.map((key) => ({ ...this.treatmentsByKey.get(key), kind: 'Treatment option' })),
        ...consultationKeys.map((key) => ({ ...this.consultationsByKey.get(key), kind: 'Consultation option' })),
      ].filter((item) => item.key);

      cards.forEach((item) => this.treatmentResults.append(this.createTreatmentCard(item)));
      this.treatmentHeading.textContent = treatmentKeys.length ? 'Treatments to discuss' : 'Recommended next step';
      this.treatmentsGroup.hidden = cards.length === 0;
    }

    createTreatmentCard(item) {
      const card = document.createElement('article');
      card.className = 'skin-quiz__treatment-card';

      const kicker = document.createElement('p');
      kicker.className = 'skin-quiz__card-kicker';
      kicker.textContent = item.kind;

      const title = document.createElement('h4');
      title.textContent = item.title;

      const description = document.createElement('p');
      description.textContent = item.description;

      const link = document.createElement('a');
      link.className = 'underlined-link';
      link.href = item.url;
      link.textContent = 'Learn more';

      card.append(kicker, title, description, link);
      return card;
    }

    onProductAction(event) {
      const button = event.target.closest('[data-add-product]');
      if (!button) return;
      this.addProducts([button.dataset.addProduct], button);
    }

    addAllProducts() {
      if (!this.currentRecommendation) return;
      this.addProducts(this.currentRecommendation.products, this.addAllButton);
    }

    async addProducts(keys, trigger) {
      const products = keys.map((key) => this.productsByKey.get(key)).filter((product) => product?.available);
      if (!products.length) return;

      const originalLabel = trigger.textContent;
      trigger.disabled = true;
      trigger.setAttribute('aria-busy', 'true');
      trigger.textContent = 'Adding…';
      this.cartStatus.hidden = true;

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: products.map((product) => ({ id: product.variantId, quantity: 1 })),
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        });

        const payload = await response.json();
        if (!response.ok || payload.status) throw new Error(payload.description || 'Products could not be added.');

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && payload.sections) {
          cartDrawer.renderContents({ ...payload, id: products[0].variantId });
        } else if (payload.sections?.['cart-icon-bubble']) {
          const parsed = new DOMParser().parseFromString(payload.sections['cart-icon-bubble'], 'text/html');
          const replacement = parsed.querySelector('#shopify-section-cart-icon-bubble');
          const current = document.querySelector('#shopify-section-cart-icon-bubble');
          if (replacement && current) current.innerHTML = replacement.innerHTML;
        }

        trigger.textContent = products.length > 1 ? 'Routine added' : 'Added';
        this.cartStatus.innerHTML = '';
        this.cartStatus.append('Added to your cart. ');
        const cartLink = document.createElement('a');
        cartLink.href = this.dataset.cartUrl;
        cartLink.textContent = 'View cart';
        this.cartStatus.append(cartLink);
        this.cartStatus.hidden = false;
      } catch (error) {
        trigger.textContent = originalLabel;
        this.cartStatus.textContent = error.message || 'Something went wrong. Please try again.';
        this.cartStatus.hidden = false;
      } finally {
        trigger.removeAttribute('aria-busy');
        if (!trigger.textContent.includes('added') && trigger.textContent !== 'Added') trigger.disabled = false;
      }
    }
  }

  if (!customElements.get('skin-questionnaire')) {
    customElements.define('skin-questionnaire', SkinQuestionnaire);
  }
})();
